import { AccelerometerInfoType } from "../enum/AccelerometerInfoType";
import { ReasonCode } from "../enum/ReasonCode";
import { IPlot, IVeriMarkerOptions } from "@interfaces/IPlot";
import { CanvasService } from "./canvas.service";
import { ColourService } from "./colour.service";
import { UnitConversionService, UnitConversionType } from "./unitConversion.service";
import { ReasonCodeService } from "./reasonCode.service";

export class VeriMarkerService {

    //private markerWidth = 24 + 12;
    //private markerHeight = 24 + 12;

    readonly defaultMarkerBuffer = 12;
    readonly defaultMarkerWidth = 20 + this.defaultMarkerBuffer;
    readonly defaultMarkerHeight = 20 + this.defaultMarkerBuffer;

    static $inject = ["unitConversionService", "reasonCodeService", "canvasService", "colourService"];

    constructor(
        private readonly unitConversionService: UnitConversionService,
        private readonly reasonCodeService: ReasonCodeService,
        private readonly canvasService: CanvasService,
        private readonly colourService: ColourService) {
    }

    getDefaultMarkerWidth() {
        return this.defaultMarkerWidth;
    }

    generateColorPip(color: string, width: number): string {
        const lineWidth = Math.floor(width / 10);
        let adjustedWidth = width - (lineWidth * 2);

        width += 1;

        //width += 4; // To allow for outer stroke

        var canvas = this.canvasService.getCachedImageCanvas(width, width, "marker.pip", color);
        if (!canvas) {
            canvas = this.canvasService.createCachedImageCanvas(width, width, "marker.pip", color);

            const context = canvas.getContext("2d");

            context.translate(0.5, 0.5);
            context.fillStyle = color;

            context.lineWidth = lineWidth * 3;
            context.strokeStyle = "white";
            this.canvasService.drawRoundRect(context, 1, 1, adjustedWidth, adjustedWidth, adjustedWidth / 2);
            context.fill();
            context.stroke();

            adjustedWidth += 2;
            context.lineWidth = lineWidth;
            context.strokeStyle = "rgba(0,0,0,.7)";
            this.canvasService.drawRoundRect(context, 0, 0, adjustedWidth, adjustedWidth, adjustedWidth / 2);
            context.stroke();

            context.translate(-0.5, -0.5);
        }
        else {
            //console.log("cache hit");
        }

        return canvas.toDataURL();
    }

    generatePip(reasonCode: ReasonCode, width: number): string {
        const lineWidth = 2;
        const adjustedWidth = width - (lineWidth * 2);

        var canvas = this.canvasService.getCachedImageCanvas(width, width, "marker.pip", reasonCode.toString());
        if (!canvas) {
            canvas = this.canvasService.createCachedImageCanvas(width, width, "marker.pip", reasonCode.toString());

            const context = canvas.getContext("2d");

            context.translate(0.5, 0.5);
            context.fillStyle = this.reasonCodeService.getBackgroundColor(reasonCode);
            context.lineWidth = lineWidth;
            context.strokeStyle = "rgba(0,0,0,.5)";
            this.canvasService.drawRoundRect(context, 1, 1, adjustedWidth, adjustedWidth, adjustedWidth / 2);
            context.fill();
            context.stroke();
            context.translate(-0.5, -0.5);
        }

        return canvas.toDataURL();
    }

    createVeriMarkerOptions(plot: IPlot, selected?: boolean): IVeriMarkerOptions {

        const markerOptions: IVeriMarkerOptions = {
            selected: selected,
            selectedColor: plot && plot.color || "rgba(0,0,0,.5)" || "rgba(0,145,234,.6)",
            reason: ReasonCode.Unknown,
            markerContents: "?",
            zIndex: selected ? 1 : 2
        };

        if (plot) {
            markerOptions.angle = plot.Direction;
            markerOptions.markerContents = plot.Velocity > 0 ? this.unitConversionService.convertToUserUnit(plot.Velocity, UnitConversionType.Speed).toString() : "0";
            markerOptions.reason = plot.Reason;
            markerOptions.time = plot.UtcMoment;

            if (plot.Reason === ReasonCode.Accelerometer && plot.AccelerometerInfo) {
                switch (plot.AccelerometerInfo.Direction) {
                    case AccelerometerInfoType.Acceleration:
                        markerOptions.markerContents = "A";
                        break;

                    case AccelerometerInfoType.Deceleration:
                        markerOptions.markerContents = "D";
                        break;

                    case AccelerometerInfoType.Cornering:
                        markerOptions.markerContents = "C";
                        break;
                }
            }

            plot.veriMarkerOptions = markerOptions;
        }

        return markerOptions;
    }

    private createUniqueMarkerCode(veriMarkerOptions: IVeriMarkerOptions): string {
        var width = veriMarkerOptions.width || this.defaultMarkerWidth;
        var height = veriMarkerOptions.height || this.defaultMarkerHeight;

        var angle = this.drawAngle(veriMarkerOptions) ? veriMarkerOptions.angle : null;
        var backgroundColor = this.reasonCodeService.getBackgroundColor(veriMarkerOptions.reason);

        return `${width}-${height}-${angle}-${veriMarkerOptions.markerContents}-${backgroundColor}-${veriMarkerOptions.selected}`;
    }

    getMarkerSize(veriMarkerOptions: IVeriMarkerOptions): number {
        return veriMarkerOptions.width || this.defaultMarkerWidth;
    }

    generateMarkerImageCanvas(veriMarkerOptions: IVeriMarkerOptions, canvas?: HTMLCanvasElement): HTMLCanvasElement {

        var width = this.getMarkerSize(veriMarkerOptions);/*veriMarkerOptions.width || this.defaultMarkerWidth*/;
        var height = this.getMarkerSize(veriMarkerOptions);/*veriMarkerOptions.height || this.defaultMarkerHeight*/;

        var markerXPos = width / 2;
        var markerYPos = Math.min(height, this.defaultMarkerHeight) / 2;

        var canvas = canvas || this.canvasService.getSingleUseCanvas(width, height);
        var context = canvas.getContext("2d");

        context.clearRect(0, 0, width, height);

        //if (veriMarkerOptions.label) {
        //    this.drawRegistration(context, veriMarkerOptions.label, veriMarkerOptions.labelIsReg, width, height);
        //}

        //console.log(height, this.markerHeight);

        this.drawMarker(context, veriMarkerOptions, markerXPos, markerYPos);

        return canvas;
    }

    generateMarkerImageUrl(veriMarkerOptions: IVeriMarkerOptions): angular.IPromise<string> {
        var markerCode = this.createUniqueMarkerCode(veriMarkerOptions);

        var existingBlobPromise = this.canvasService.getBlobCachePromise(markerCode);

        if (existingBlobPromise) {
            return existingBlobPromise;
        }

        var canvas = this.generateMarkerImageCanvas(veriMarkerOptions);

        return this.canvasService.getBlobUrlPromise(canvas, markerCode);

        //var dataURI = canvas.toDataURL();
        //return dataURI;
    }

    private drawAngle(veriMarkerOptions: IVeriMarkerOptions) {
        const isNumeric = !isNaN(parseFloat(veriMarkerOptions.markerContents)) && isFinite(veriMarkerOptions.markerContents as any);
        const drawAngle = veriMarkerOptions.markerContents != "0" && isNumeric && veriMarkerOptions.reason != ReasonCode.Unknown;

        return drawAngle;
    }

    drawMarker(context: CanvasRenderingContext2D, veriMarkerOptions: IVeriMarkerOptions, x: number, y: number) {
        window.performance.mark("drawMarker");
        var markerDiameter = 24;
        var markerRadius = markerDiameter / 2;

        const drawAngle = this.drawAngle(veriMarkerOptions);
        const angle = veriMarkerOptions.angle + 45;

        // Draw reason code colour
        this.drawMarkerShape(context, x, y, markerDiameter, drawAngle, angle);

        // Shadow
        context.lineWidth = veriMarkerOptions.selected ? 6 : 4;
        //var transparentColor = this.colourService.createTransparentColor(veriMarkerOptions.selectedColor, 0.7);
        context.strokeStyle = veriMarkerOptions.selected ? /*transparentColor*/veriMarkerOptions.selectedColor : "rgba(0,0,0,.1)";
        //context.strokeStyle = veriMarkerOptions.selected ? transparentColor : "rgba(255,255,255,.99)";
        context.stroke();

        // Fill
        context.fillStyle = this.reasonCodeService.getBackgroundColor(veriMarkerOptions.reason);
        context.fill();

        // Border
        context.lineWidth = 1;
        context.strokeStyle = veriMarkerOptions.selected ? "#555" : "rgba(0,0,0,.5)";
        context.stroke();

        context.font = "400 11px Roboto";
        context.textAlign = "center";
        context.fillStyle = this.reasonCodeService.getForegroundColor(veriMarkerOptions.reason);

        const textToFill = veriMarkerOptions.markerContents || "--";
        context.fillText(textToFill, x, y + 4);

        //if (!plotMarkerOptions.markerContents) {
        //    //console.error(textToFill, plotMarkerOptions);
        //    console.error("plotMarkerOptions.markerContents", plotMarkerOptions.markerContents);
        //}
        window.performance.measure("drawMarker");
    }

    drawMarkerShape(context: CanvasRenderingContext2D, x: number, y: number, width: number, drawAngle: boolean, angle: number) {

        const drawingX = - (width / 2);
        const drawingY = - (width / 2);

        if (drawAngle) {
            context.translate(x, y);
            this.canvasService.rotateContext(context, angle);

            const cornerRadius = Math.ceil(width / 2);
            this.canvasService.drawRoundRect(context, drawingX, drawingY, width, width, { tl: drawAngle ? 0 : cornerRadius, tr: cornerRadius, bl: cornerRadius, br: cornerRadius });

            this.canvasService.rotateContext(context, -angle);
            context.translate(-x, -y);
        }
        else {
            const radius = width / 2;
            context.beginPath();
            context.arc(x, y, radius, 0, Math.PI * 2, false);
            context.closePath();
        }
    }

    drawRegistration(context: CanvasRenderingContext2D, label: string, labelIsReg: boolean, width: number, height: number) {
        var regHeight = 18;
        var maxRegLength = 12;

        let canvas = this.canvasService.getCachedImageCanvas(width, regHeight, "registration", labelIsReg.toString());
        if (!canvas) {
            canvas = this.canvasService.createCachedImageCanvas(width, regHeight, "registration", labelIsReg.toString());
            const registrationContext = canvas.getContext("2d");
            registrationContext.translate(0.5, 0.5);

            registrationContext.fillStyle = labelIsReg ? "#ffdd21" : "#ffffff";
            registrationContext.strokeStyle = "rgba(0, 0, 0, 0.4)";
            registrationContext.lineWidth = 1;
            this.canvasService.drawRoundRect(registrationContext, 0, 0, width - 1, regHeight - 1, 3);

            registrationContext.fill();
            registrationContext.stroke();

            registrationContext.translate(-0.5, -0.5);
        }

        context.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, height - regHeight - 1, width, regHeight);

        if (label && label.length > maxRegLength) {
            label = label.substr(0, maxRegLength - 3) + "...";
        }

        context.font = "500 12px Roboto";
        context.textAlign = "center";
        context.fillStyle = "#333";
        context.fillText(label.toUpperCase(), width / 2, height - 6);
    }

    //generateClusterImage(width: number, percentage: number, colour1: string = null, colour2: string = null): string {
    //    var utilisationColor = colour1 || "red";
    //    var nonUtilisationColor = colour2 || "grey";

    //    const canvas = this.canvasService.getSingleUseCanvas(width, width);
    //    const context = canvas.getContext("2d");

    //    //context.fillStyle = "rgb(220,220,220)";
    //    //context.fillRect(0, 0, width, width);

    //    context.translate(0.5, 0.5);
    //    context.fillStyle = "white";
    //    context.lineWidth = 2;
    //    context.strokeStyle = "#555";

    //    var x = width / 2;
    //    var y = width / 2;
    //    //var adjustedWidth = width - 30;
    //    var adjustedWidth = 33;
    //    var adjustedRadius = adjustedWidth / 2;

    //    // White background
    //    //context.beginPath();
    //    //context.arc(x, y, adjustedRadius, 0, Math.PI * 2, false);
    //    //context.closePath();

    //    //context.fill();
    //    //context.stroke();

    //    var max = 2 * Math.PI;
    //    var radiusModifier = max / 100;

    //    //console.log(percentage * radiusModifier, utilisationColor);

    //    var utilisationArcStart = 0;
    //    var utilisationArcEnd = percentage * radiusModifier;
    //    var nonUtilisationArcStart = utilisationArcEnd;
    //    var nontUtilisationArcEnd = max;

    //    // Outer border
    //    this.strokeArc(context, x, y, adjustedRadius + 4, "#555", 0, max);

    //    // Utilisation arcs
    //    this.strokeArc(context, x, y, adjustedRadius + 3, utilisationColor, utilisationArcStart, utilisationArcEnd);

    //    // Non utilisation arc
    //    this.strokeArc(context, x, y, adjustedRadius + 3, nonUtilisationColor, nonUtilisationArcStart, nontUtilisationArcEnd);

    //    // Reset canvas
    //    context.translate(-0.5, -0.5);

    //    return canvas.toDataURL();
    //}

    //private strokeArc(context: CanvasRenderingContext2D, x: number, y: number, radius: number, color: string, startAngle: number, endAngle: number) {
    //    context.beginPath();
    //    context.lineWidth = 4;
    //    context.strokeStyle = color;
    //    context.arc(x, y, radius, startAngle, endAngle, false);
    //    context.stroke();
    //}
}
